import { Injectable } from "@angular/core";
import { EMPTY, expand, Observable, of, switchMap, takeWhile, throwError, timer } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { ElementsService } from "./elements.service";
import { PropertyStatus } from "../../model/property-status";
import { IPropertyResponse } from "../../interfaces/i-property-response";
import { IDamageAssessmentResponse } from "../../interfaces/i-damage-assessment-response";
import { IPropertyDataState } from "../../interfaces/i-property-data-state";
import { IPropertyStructure } from "../../interfaces/i-property-structure";

const MAX_POLL_RETRIES = 10;
const POLL_INTERVAL_MS = 3000;
const COMPLETED_STATUSES = ["Completed", "Delivered"];

@Injectable({
  providedIn: "root",
})
export class PropertyDataService {
  constructor(private elementsService: ElementsService) {}

  /**
   * Obtiene los datos de property para el WKT dado.
   * Gestiona internamente el flujo on-demand con polling.
   *
   * Emite { status: downloading } inmediatamente, luego:
   * - { status: finished, data } cuando los datos están disponibles
   * - { status: error, error } si la petición falla o se agotan los reintentos
   *
   * @param wkt - Punto en formato WKT (ej: "POINT(-73.9857 40.7484)")
   */
  fetchPropertyInfo(wkt: string): Observable<IPropertyDataState<IPropertyResponse>> {
    return new Observable((observer) => {
      observer.next({ status: PropertyStatus.downloading });

      const sub = this.elementsService
        .requestPropertyInformation(wkt, true)
        .pipe(
          switchMap((data: IPropertyResponse) => {
            if (data.status === 0 || data.status === 200) {
              return this.pollPropertyStatus(data);
            }
            if (!data.status) {
              return of(data);
            }
            return throwError(() => new Error(`Unexpected status: ${data.status}`));
          }),
          map((data: IPropertyResponse) => {
            this.sortStructures(data.structures);
            return { status: PropertyStatus.finished, data } as IPropertyDataState<IPropertyResponse>;
          }),
          catchError((err) => {
            console.error("[PropertyDataService] fetchPropertyInfo error:", err);
            return of({ status: PropertyStatus.error, error: err?.message ?? "Unknown error" } as IPropertyDataState<IPropertyResponse>);
          }),
        )
        .subscribe(observer);

      return () => sub.unsubscribe();
    });
  }

  /**
   * Obtiene los datos de damage assessment para el WKT dado.
   *
   * Emite { status: downloading } inmediatamente, luego:
   * - { status: finished, data } cuando los datos están disponibles
   * - { status: error, error } si la petición falla
   *
   * @param wkt - Punto en formato WKT (ej: "POINT(-73.9857 40.7484)")
   */
  fetchDamageAssessment(wkt: string): Observable<IPropertyDataState<IDamageAssessmentResponse>> {
    return new Observable((observer) => {
      observer.next({ status: PropertyStatus.downloading });

      const sub = this.elementsService
        .requestDamageInformation(wkt)
        .pipe(
          map((data: IDamageAssessmentResponse) => {
            this.sortDamageStructures(data);
            return { status: PropertyStatus.finished, data } as IPropertyDataState<IDamageAssessmentResponse>;
          }),
          catchError((err) => {
            console.error("[PropertyDataService] fetchDamageAssessment error:", err);
            return of({ status: PropertyStatus.error, error: err?.message ?? "Unknown error" } as IPropertyDataState<IDamageAssessmentResponse>);
          }),
        )
        .subscribe(observer);

      return () => sub.unsubscribe();
    });
  }

  /**
   * Polling interno para flujo on-demand: consulta el estado cada POLL_INTERVAL_MS
   * hasta que el servidor devuelva "Completed" o "Delivered", o se agoten los reintentos.
   * Invisible para el consumidor — solo `fetchPropertyInfo` lo utiliza.
   *
   * @param initialResponse - Respuesta inicial que contiene el request_id
   */
  private pollPropertyStatus(initialResponse: IPropertyResponse): Observable<IPropertyResponse> {
    let retriesLeft = MAX_POLL_RETRIES;

    return this.elementsService.requestPropertyStatus(initialResponse.request_id).pipe(
      expand((pollResponse) => {
        if (COMPLETED_STATUSES.includes(pollResponse.status)) {
          return EMPTY;
        }
        if (retriesLeft > 0) {
          retriesLeft--;
          return timer(POLL_INTERVAL_MS).pipe(
            switchMap(() => this.elementsService.requestPropertyStatus(initialResponse.request_id)),
          );
        }
        return throwError(() => new Error("Max retries exceeded"));
      }),
      takeWhile((pollResponse) => !COMPLETED_STATUSES.includes(pollResponse.status), true),
      switchMap((pollResponse) => {
        if (!COMPLETED_STATUSES.includes(pollResponse.status)) {
          return EMPTY;
        }
        return this.elementsService.downloadPropertyInfo(initialResponse.request_id).pipe(
          map((propertyInfo: IPropertyResponse) => {
            if (!propertyInfo.structures) {
              throw new Error("No structures found in downloaded property info");
            }
            propertyInfo.latitude = initialResponse.latitude;
            propertyInfo.longitude = initialResponse.longitude;
            return propertyInfo;
          }),
        );
      }),
    );
  }

  /**
   * Ordena las estructuras: primary siempre primera, luego por área descendente.
   * @param structures - Array de estructuras a ordenar (mutación in-place)
   */
  private sortStructures(structures: IPropertyStructure[]): void {
    if (!structures?.length) return;
    structures.sort((a, b) => {
      if (a.primaryStructure && !b.primaryStructure) return -1;
      if (!a.primaryStructure && b.primaryStructure) return 1;
      return b.footPrint.area - a.footPrint.area;
    });
  }

  /**
   * Ordena las estructuras de damage assessment manteniendo la correspondencia
   * pre/post catastrophe por índice tras el reordenamiento.
   * @param response - Respuesta de damage assessment (mutación in-place)
   */
  private sortDamageStructures(response: IDamageAssessmentResponse): void {
    if (!response?.pre_catastrophe?.structure?.length) return;

    const combined = response.pre_catastrophe.structure.map((item, index) => ({
      pre: item,
      post: response.post_catastrophe.structure[index],
    }));

    combined.sort((x, y) => {
      if (x.pre.primaryStructure && !y.pre.primaryStructure) return -1;
      if (!x.pre.primaryStructure && y.pre.primaryStructure) return 1;
      return y.pre.footPrint.area - x.pre.footPrint.area;
    });

    response.pre_catastrophe.structure = combined.map((pair) => pair.pre);
    response.post_catastrophe.structure = combined.map((pair) => pair.post);
  }
}
